import React, { useCallback, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { useNavigate } from "react-router-dom";

// Icons
import { Search, CheckCircle, Loader, X, Plus } from "lucide-react";

import { ToastContainer } from "react-toastify";

import useUserStats from "hooks/useUserStats";

import useRefferalls from "hooks/useRefferrals";
import { User } from "types/user.type";

const filterKeys = {
  totalMynda: "total-myndas",
  totalEmployers: "total-employers",
  approvedMyndas: "approved-myndas",
  approvedEmployers: "approved-employers",
  myndaReview: "mynda-review",
  employerReview: "employer-review",
  rejectedMynda: "rejected-mynda",
  rejectedEmployer: "rejectedEmployer",
  totalUsers: "totalUsers",
};

interface Props {
  user: User;
}
const Refferalls = ({ user }: Props) => {
  const [showForm, setShowForm] = useState(false);
  const { data, isLoading, onQueryChange, onFilter } = useRefferalls(
    user?.uniqueId
  );

  const navigate = useNavigate();

  const handleFilter = (filter: string) => {
    switch (filter) {
      case filterKeys.totalMynda:
        onFilter({
          search: "Mynda",
          page: 1,
          limit: 50,
        });

        break;

      case filterKeys.totalEmployers:
        onFilter({
          search: "Employer",
          page: 1,
          limit: 50,
        });

        break;
      case filterKeys.approvedMyndas:
        onFilter({
          search: "Mynda",
          kycStatus: "Approved",
          page: 1,
          limit: 50,
        });

        break;
      case filterKeys.approvedEmployers:
        onFilter({
          search: "Employer",
          kycStatus: "Approved",
          page: 1,
          limit: 50,
        });

        break;
      case filterKeys.myndaReview:
        onFilter({
          search: "Mynda",
          kycStatus: "In Review",
          page: 1,
          limit: 50,
        });

        break;

      case filterKeys.employerReview:
        onFilter({
          search: "Employer",
          kycStatus: "In Review",
          page: 1,
          limit: 50,
        });

        break;

      case filterKeys.rejectedEmployer:
        onFilter({
          search: "Employer",
          kycStatus: "Rejected",
          page: 1,
          limit: 50,
        });

        break;

      case filterKeys.rejectedMynda:
        onFilter({
          search: "Mynda",
          kycStatus: "Rejected",
          page: 1,
          limit: 50,
        });

        break;

      case filterKeys.totalUsers:
        onFilter({
          search: "",
          kycStatus: "",
          page: 1,
          limit: 50,
        });

        break;

      default:
        return;
    }
  };

  // columns
  const Status = ({ item }: any) => {
    switch (item) {
      case "Approved":
        return (
          <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status">
            <CheckCircle className="size-3 mr-1.5" />
            {item}
          </span>
        );
      case "In Review":
        return (
          <span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-slate-100 border-transparent text-slate-500 dark:bg-slate-500/20 dark:text-zink-200 dark:border-transparent status">
            <Loader className="size-3 mr-1.5" />
            {item}
          </span>
        );
      case "Rejected":
        return (
          <span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-red-100 border-transparent text-red-500 dark:bg-red-500/20 dark:border-transparent status">
            <X className="size-3 mr-1.5" />
            {item}
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-yellow-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status">
            {/* <CheckCircle className="size-3 mr-1.5" /> */}
            {item}
          </span>
        );
    }
  };

  const viewUser = (user: User) => {
    navigate(`/pages-account/${user._id}`);
  };

  const handlePaginate = (page: number) => {
    onQueryChange("page", page);
  };

  const handleSearch = (page: string) => {
    onQueryChange("search", page);
  };

  const toggleAdd = () => {
    setShowForm(!showForm);
  };

  return (
    <React.Fragment>
      <BreadCrumb title="Referred users" pageTitle="Users" />

      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <div className="card" id="usersTable">
            <input
              onChange={({ target }) => handleSearch(target.value)}
              placeholder="Search first name, last name, role, status(Pending, Approved, In Review Rejected)"
              className="w-[50%] p-3 h-[50px] border border-gray-500 rounded-md m-4"
            />
            <div className="overflow-x-auto">
              <table data-stack className="w-full">
                <thead className="ltr:text-left rtl:text-right ">
                  <tr>
                    <th className="px-3.5 py-2.5 font-semibold">Name</th>
                    <th className="px-3.5 py-2.5 font-semibold  border-custom-500 dark:border-custom-800">
                      Email Address
                    </th>
                    <th className="px-3.5 py-2.5 font-semibold  border-custom-500 dark:border-custom-800">
                      Phone Number
                    </th>
                    <th className="px-3.5 py-2.5 font-semibold  border-custom-500 dark:border-custom-800">
                      Date Joined
                    </th>
                    <th className="px-3.5 py-2.5 font-semibold  border-custom-500 dark:border-custom-800">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="">
                  {data?.data?.total! < 1 ? (
                    <div className="noresult">
                      <div className="py-6 text-center">
                        <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                        <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                      </div>
                    </div>
                  ) : (
                    <>
                      {data?.data?.data.map((user) => (
                        <tr
                          key={user._id}
                          className=" cursor-pointer"
                          onClick={() => viewUser(user)}
                        >
                          <td data-label="Name" className="px-3.5 py-2.5  border-custom-500 dark:border-custom-800">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center justify-center size-10 font-medium rounded-full shrink-0 bg-slate-200 text-slate-800 dark:text-zink-50 dark:bg-zink-600">
                                <img
                                  src=""
                                  alt=""
                                  className="h-10 rounded-full"
                                />
                              </div>
                              <div className="grow">
                                <h6 className="mb-1">
                                  {user.firstName} {user.lastName}
                                </h6>
                                <p className="text-slate-500 dark:text-zink-200">
                                  {user.role}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td data-label="Email Address" className="px-3.5 py-2.5  border-custom-500 dark:border-custom-800">
                            {user.email}
                          </td>
                          <td data-label="Phone Number" className="px-3.5 py-2.5 flex items-center gap-3  border-custom-500 dark:border-custom-800">
                            {user.phoneNumber}{" "}
                            {user.isPhoneVerified ? (
                              <span className=" b border-transparent text-green-500">
                                <CheckCircle className="size-3 mr-1.5" />
                              </span>
                            ) : (
                              <span className=" text-slate-500">
                                <Loader className="size-3 mr-1.5" />
                              </span>
                            )}
                          </td>
                          <td data-label="Date Joined" className="px-3.5 py-2.5  border-custom-500 dark:border-custom-800">
                            {new Date(user.createdAt).toLocaleDateString()}{" "}
                            {new Date(user.createdAt).toLocaleTimeString()}
                          </td>
                          <td data-label="Status" className="px-3.5 py-2.5  border-custom-500 dark:border-custom-800">
                            <Status item={user.kycStatus} />
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
              <div className="flex gap-4 items-center justify-end my-4 px-4">
                <button
                  disabled={Number(data?.data?.page)! <= 1}
                  className="p-3 border-sm"
                  onClick={() => handlePaginate(Number(data?.data?.page!)! - 1)}
                >
                  Previous
                </button>
                <span>{data?.data?.page}</span>
                <button
                  disabled={
                    Number(data?.data?.page)! >=
                    Number(data?.data?.total) / Number(data?.data?.limit)!
                  }
                  onClick={() => handlePaginate(Number(data?.data?.page!) + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Refferalls;
